import { toManagedError } from '../errors/managed';

export type LoggerMethodWithoutLevel = (message: string, fields?: Record<string, any>) => void;
export type LoggerMethod = (level: LogLevel, message: string, fields?: Record<string, any>) => void;

export type Logger = {
  log: LoggerMethodWithoutLevel;
  debug: LoggerMethodWithoutLevel;
  info: LoggerMethodWithoutLevel;
  warn: LoggerMethodWithoutLevel;
  error: LoggerMethodWithoutLevel;
  createChild: (options: {
    prefix?: string;
    fields?: Record<string, unknown>;
    request?: Request | null;
    level?: LogLevel | LogLevelEnum;
  }) => Logger;
};

export type LogLevelEnum = keyof typeof LogLevel;

export type LogFormat = 'text' | 'json';

export type LoggerIntegration = {
  onMessage: LoggerMethod;
};

export type RequestAwareLoggerIntegration = LoggerIntegration & {
  withRequest: (req: Request) => LoggerIntegration;
};

export type LoggerOptions = {
  integrations?: LoggerIntegration[];
  request?: Request;
  fields?: Record<string, any>;
  format?: LogFormat;
};

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

const LOG_LEVEL_NAMES: Record<LogLevel, string> = {
  [LogLevel.DEBUG]: 'debug',
  [LogLevel.INFO]: 'info',
  [LogLevel.WARN]: 'warning',
  [LogLevel.ERROR]: 'error',
};

type JSONEntry = {
  timestamp: string;
  level: string;
  logger?: string;
  message?: string;
  [p: string]: any;
};

export class ConsoleLogger {
  private _level: LogLevel = LogLevel.DEBUG;
  private readonly integrations: LoggerIntegration[];
  private readonly fields: Record<string, any>;
  private readonly request?: Request;
  private readonly rawPrefix: string;
  private readonly format: LogFormat;

  constructor(prefix: string = '', level: LogLevel | LogLevelEnum = 'INFO', options?: LoggerOptions) {
    this.rawPrefix = prefix;
    this.setLevel(level);
    this.request = options?.request;
    this.fields = { ...(options?.fields || {}) };
    this.integrations = [...(options?.integrations || [])];
    this.format = options?.format ?? 'text';
  }

  get level(): LogLevel {
    return this._level;
  }

  public setLevel(level: LogLevel | LogLevelEnum): void {
    this._level = typeof level === 'number' ? level : LogLevel[(level as LogLevelEnum)?.toUpperCase() as LogLevelEnum];
  }

  public addIntegration(integration: LoggerIntegration): void {
    this.integrations.push(integration);
  }

  private logMessage(level: LogLevel, message: string, fields?: Record<string, any>): void {
    try {
      this.doLogMessage(level, message, fields);
    } catch (error) {
      console.error('error logging message (inside logger)', error);
    }
  }

  private doLogMessage(level: LogLevel, message: string, fields?: Record<string, any>): void {
    if (level >= this._level) {
      let consoleMethod: (...args: any[]) => void;
      let levelTag: string;

      if (fields instanceof Error) {
        fields = { error: fields };
      }
      if (fields !== null && fields !== undefined && typeof fields !== 'object') {
        fields = { value: fields };
      }

      switch (level) {
        case LogLevel.DEBUG:
          consoleMethod = console.debug;
          levelTag = '[debug]';
          break;
        case LogLevel.INFO:
          consoleMethod = console.info;
          levelTag = '[info]';
          break;
        case LogLevel.WARN:
          consoleMethod = console.warn;
          levelTag = '[WARNING]';
          break;
        case LogLevel.ERROR:
          consoleMethod = console.error;
          levelTag = '[ERROR]';
          break;
        default:
          consoleMethod = console.info;
          levelTag = '[???]';
          break;
      }

      if (this.format === 'json') {
        const mergedFields =
          Object.keys(this.fields).length || (fields && Object.keys(fields).length) ? { ...this.fields, ...fields } : undefined;

        const entry: JSONEntry = {
          timestamp: new Date().toISOString(),
          level: LOG_LEVEL_NAMES[level],
          logger: this.rawPrefix || undefined,
          message,
          ...mergedFields,
        };

        consoleMethod(
          JSON.stringify(entry, (_key, value) => {
            if (value instanceof Error) return toManagedError(value).toObject();
            return value;
          }),
        );
      } else {
        const formattedPrefix = this.rawPrefix ? `[${this.rawPrefix}]` : '';

        const allFields = {
          ...this.fields,
          ...(fields ?? {}),
        };

        consoleMethod(levelTag, formattedPrefix, message, ...(Object.keys(allFields).length ? [allFields] : []));
      }

      this.integrations?.forEach((integration) => integration.onMessage(level, message, fields));
    }
  }

  createChild(options: {
    prefix?: string;
    fields?: Record<string, unknown>;
    request?: Request | null;
    level?: LogLevel | LogLevelEnum;
  }): ConsoleLogger {
    const childRawPrefix = options.prefix ? (this.rawPrefix ? `${this.rawPrefix}.${options.prefix}` : options.prefix) : this.rawPrefix;

    const childBoundFields: Record<string, unknown> = {
      ...this.fields,
      ...(options.fields ?? {}),
    };

    const childIntegrations = options.request
      ? this.integrations?.map((integration) => {
          const maybeRequestAware = integration as Partial<RequestAwareLoggerIntegration>;
          return typeof maybeRequestAware.withRequest === 'function' ? maybeRequestAware.withRequest(options.request!) : integration;
        })
      : [...(this.integrations || [])];

    const childRequest = options?.request === undefined ? this.request : options?.request === null ? undefined : options?.request;

    return new ConsoleLogger(childRawPrefix, options?.level || this._level, {
      integrations: childIntegrations,
      fields: childBoundFields,
      request: childRequest,
      format: this.format,
    });
  }

  log(message: string, fields?: Record<string, any>): void {
    this.logMessage(LogLevel.DEBUG, message, fields);
  }

  debug(message: string, fields?: Record<string, any>): void {
    this.logMessage(LogLevel.DEBUG, message, fields);
  }

  info(message: string, fields?: Record<string, any>): void {
    this.logMessage(LogLevel.INFO, message, fields);
  }

  warn(message: string, fields?: Record<string, any>): void {
    this.logMessage(LogLevel.WARN, message, fields);
  }

  error(message: string, fields?: Record<string, any>): void {
    this.logMessage(LogLevel.ERROR, message, fields);
  }

  isDebugEnabled(): boolean {
    return this._level <= LogLevel.DEBUG;
  }

  isInfoEnabled(): boolean {
    return this._level <= LogLevel.INFO;
  }

  isWarnEnabled(): boolean {
    return this._level <= LogLevel.WARN;
  }

  isErrorEnabled(): boolean {
    return this._level <= LogLevel.ERROR;
  }
}
